import { useEffect, useState } from 'react';

import { StreamFetchError } from 'api/api.config';
import type { CurrentUserData } from 'hooks/api/use-current-user';

import { useTokenUsage } from './use-token-usage';

import {
  getIsTokenUsageExempt,
  getTokenLimitReachedMessage,
} from '../helpers/token-usage-helpers';

import { USAGE_LIMIT_REACHED_ERROR_TYPE } from '../ai-assistant.constants';

const EMPTY_PERCENT = 0;

const isUsageLimitError = (error: unknown): boolean =>
  error instanceof StreamFetchError &&
  error.data?.type === USAGE_LIMIT_REACHED_ERROR_TYPE;

export interface UseTokenLimitResult {
  isBlocked: boolean;
  blockResetDate?: string;
  limitReachedMessage: string;
  isUsageLimitError: (error: unknown) => boolean;
  resetLimitFlag: () => void;
  registerLimitError: () => void;
}

/**
 * Owns the AI token soft-block policy
 *
 * The block is triggered by either the meter reading 0% (proactive) or a
 * `usage-limit-reached` send error on message send.
 */
export const useTokenLimit = (
  currentUser: CurrentUserData | null | undefined
): UseTokenLimitResult => {
  const isTokenUsageExempt = getIsTokenUsageExempt(currentUser);

  const {
    isLimitReached,
    hasUsageData,
    percentRemaining,
    resetDate,
    isResetDateInFuture,
  } = useTokenUsage({
    oktaUserId: currentUser?.oktaUserId ?? '',
    enabled: !isTokenUsageExempt,
  });

  const [didLastSendHitLimit, setDidLastSendHitLimit] = useState(false);

  const isBlocked =
    !isTokenUsageExempt && (isLimitReached || didLastSendHitLimit);
  const blockResetDate = isResetDateInFuture ? resetDate : undefined;

  useEffect(() => {
    if (hasUsageData && percentRemaining > EMPTY_PERCENT) {
      setDidLastSendHitLimit(false);
    }
  }, [hasUsageData, percentRemaining]);

  return {
    isBlocked,
    blockResetDate,
    limitReachedMessage: getTokenLimitReachedMessage(blockResetDate),
    isUsageLimitError,
    resetLimitFlag: () => setDidLastSendHitLimit(false),
    registerLimitError: () => setDidLastSendHitLimit(true),
  };
};
